import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/kfmApi";
import { Button } from "@/components/ui/button";
import SermonEditor from "@/components/SermonEditor";
import { ScrollText, Loader2, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Generate + edit + export a sermon outline from a non-study source (a Go Deeper
// conversation or a saved prayer). Generation is explicit (a button) so credits are
// never spent just by opening the dialog.
export default function SermonComposer({ open, onOpenChange, endpoint, body, title, description, cost }) {
  const navigate = useNavigate();
  const [sermon, setSermon] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ready | locked | error
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!open) {
      setSermon("");
      setStatus("idle");
      setErrMsg("");
    }
  }, [open]);

  const generate = async () => {
    setStatus("loading");
    try {
      const { data } = await api.post(endpoint, body);
      setSermon(data.sermon);
      setStatus("ready");
      toast.success(`Sermon outline ready \u00b7 ${data.cost} credits used`);
    } catch (e) {
      const code = e.response?.status;
      setErrMsg(formatApiError(e.response?.data?.detail));
      setStatus(code === 402 || code === 404 ? "locked" : "error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sermon-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            <ScrollText className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} /> {title || "Sermon outline"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            {description || "Shape this into a preachable outline you can edit and export."}
          </DialogDescription>
        </DialogHeader>

        {status === "idle" && (
          <div className="py-6 text-center space-y-4">
            <p className="text-sm text-slate-400 leading-relaxed">
              Let the passage drive the structure — a clear big idea, supporting movements grounded in
              the text, application, and a closing.
            </p>
            <Button
              data-testid="composer-generate"
              onClick={generate}
              className="rounded-full text-white"
              style={{ backgroundColor: "#00E5FF" }}
            >
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Create sermon outline{cost ? ` \u00b7 ${cost} credits` : ""}
            </Button>
          </div>
        )}

        {status === "loading" && (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.5} />
            <span className="text-sm">Composing your outline&hellip;</span>
          </div>
        )}

        {status === "locked" && (
          <div className="py-8 text-center space-y-3" data-testid="composer-locked">
            <Lock className="w-7 h-7 mx-auto text-slate-500" strokeWidth={1.5} />
            <p className="text-sm text-slate-400 leading-relaxed">{errMsg}</p>
            <button data-testid="composer-upgrade" onClick={() => navigate("/billing")} className="text-sm text-[#00E5FF] underline">
              See plans
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="py-8 text-center space-y-3" data-testid="composer-error">
            <p className="text-sm text-slate-400">{errMsg || "Something went wrong. Please try again."}</p>
            <Button variant="outline" className="rounded-full" data-testid="composer-retry" onClick={generate}>Try again</Button>
          </div>
        )}

        {status === "ready" && (
          <SermonEditor sermon={sermon} onSermonChange={setSermon} title={title} testid="composer-sermon" />
        )}
      </DialogContent>
    </Dialog>
  );
}
